import { ChangeEvent, FormEvent, MouseEvent, useEffect, useState } from "react";

import {
  useInput,
  useArray,
  useProvidersEmployeeLayout,
  useApiManagersEmployeeConditionSubmissionsIndex,
  usePostSortType,
  useCompleteStatusDropdown,
  useDateRange,
  useMultipleDropdown,
  useApisMembersEmployeeConditionSubmissionsSearchItemsIndex,
  useMultiDivisionMultiSectionDropdown,
  useProvidersCurrentEmployee,
  useBoolean,
} from "~/hooks";

import {
  EmployeeConditionSubmissionType,
  ApiMembersEmployeeConditionSubmissionsIndexRequestType,
  SortType,
  OptionType,
  SingleValueType,
  RangeDatePropsType,
  MultiValueType,
  ChildType,
  ParentType,
} from "~/domains";

type ReturnType = {
  keyword: string;
  isFetching: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  startDate: Date | null;
  endDate: Date | null;
  employeeConditionSubmissions: EmployeeConditionSubmissionType[];
  sort: SortType;
  selectableEmployeeConditionSurveys: OptionType[];
  selectedEmployeeConditionSurveys: MultiValueType<OptionType>;
  onChangeEmployeeConditionSurveys: (
    newValue: MultiValueType<OptionType>,
  ) => void;
  onDivisionChange: (value: ParentType[]) => void;
  onSectionChange: (value: ChildType[]) => void;
  onEmployeesChange: (value: ParentType[]) => void;
  onArchivedEmployeesChange: (value: ParentType[]) => void;
  selectedDropdownEmployees: ParentType[];
  selectableDropdownEmployees: ParentType[];
  selectableDropdownDivisions: ParentType[];
  selectedDropdownDivisions: ParentType[];
  selectableDropdownSections: ChildType[];
  selectedDropdownSections: ChildType[];
  selectedDropdownArchivedEmployees: ParentType[];
  selectableDropdownArchivedEmployees: ParentType[];
  selectableDropdownHasBookmarkIds: OptionType[];
  selectedDropdownHasBookmarkId?: SingleValueType<OptionType>;
  onChangeDropdownHasBookmarkId: (value: SingleValueType<OptionType>) => void;
  selectableDropdownHasReplyIds: OptionType[];
  selectedDropdownHasReplyId?: SingleValueType<OptionType>;
  onChangeDropdownHasReplyId: (value: SingleValueType<OptionType>) => void;
  isBelongingEmployee: boolean;
  onChangeIsBelongingEmployee: (e: ChangeEvent<HTMLInputElement>) => void;
  setSort: (val: SortType) => void;
  onChangeDateRange: (value: RangeDatePropsType) => void;
  onChangeKeyword: (e: ChangeEvent<HTMLInputElement>) => void;
  onSearchSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onConditionReset: (e: MouseEvent<HTMLButtonElement>) => void;
  refetch: () => void;
};

export const useSearchManagersEmployeeConditionSubmissions = (): ReturnType => {
  const { currentEmployee } = useProvidersCurrentEmployee();
  const { sort, snakeKeyValue, setSort } = usePostSortType();
  const { setFalseRightSidebarMain } = useProvidersEmployeeLayout();
  const { items, setItems } = useArray<EmployeeConditionSubmissionType>([]);

  const [{ value: keyword, onChange: onChangeKeyword }, setKeyword] =
    useInput("");
  const {
    isChecked: isBelongingEmployee,
    onChange: onChangeIsBelongingEmployee,
    setFalse: setBelongingEmployeeFalse,
  } = useBoolean(true);
  const [
    {
      startDate,
      formattedStartDate,
      endDate,
      formattedEndDate,
      onChange: onChangeDateRange,
    },
  ] = useDateRange();
  const [
    {
      statusValue: hasBookmark,
      selectedDropdownCompleteStatusId: selectedDropdownHasBookmarkId,
      selectableDropdownCompleteStatusIds: selectableDropdownHasBookmarkIds,
      onChangeDropdownCompleteStatusId: onChangeDropdownHasBookmarkId,
    },
  ] = useCompleteStatusDropdown({
    initialStatusNames: {
      true: "ブックマークあり",
      false: "ブックマークなし",
    },
  });
  const [
    {
      statusValue: hasReply,
      selectedDropdownCompleteStatusId: selectedDropdownHasReplyId,
      selectableDropdownCompleteStatusIds: selectableDropdownHasReplyIds,
      onChangeDropdownCompleteStatusId: onChangeDropdownHasReplyId,
    },
  ] = useCompleteStatusDropdown({
    initialStatusNames: {
      true: "リプライあり",
      false: "リプライなし",
    },
  });
  const [
    {
      dropdownSelectableValue: selectableEmployeeConditionSurveys,
      dropdownValue: selectedEmployeeConditionSurveys,
      onChange: onChangeEmployeeConditionSurveys,
      setSelectableOption: setSelectableEmployeeConditionSurveys,
    },
  ] = useMultipleDropdown();
  useApisMembersEmployeeConditionSubmissionsSearchItemsIndex({
    config: {
      enabled: !!currentEmployee,
      onSuccess: (data) => {
        setSelectableEmployeeConditionSurveys(
          data.employeeConditionSurveys.map((survey) => ({
            id: survey.id,
            name: survey.title,
          })),
        );
      },
    },
  });

  const {
    selectableDivisions,
    selectedDivisions,
    onDivisionChange,
    optionSelectableSections,
    optionSelectedSections,
    onSectionChange,
    selectableEmployees,
    selectedEmployees,
    onEmployeesChange,
    selectableArchivedEmployees,
    selectedArchivedEmployees,
    onArchivedEmployeesChange,
  } = useMultiDivisionMultiSectionDropdown({
    selectableDivisions: currentEmployee?.businessDivisions,
    selectableSections: currentEmployee?.businessSections,
    isSelectEmployee: true,
  });

  const [params, setParams] =
    useState<ApiMembersEmployeeConditionSubmissionsIndexRequestType>({
      sortType: snakeKeyValue,
      isBelongingEmployee,
    });

  const { isFetching, fetchNextPage, hasNextPage, refetch } =
    useApiManagersEmployeeConditionSubmissionsIndex({
      params,
      config: {
        onSuccess: (data) => {
          setItems(
            data.pages.map((page) => page.employeeConditionSubmissions).flat(),
          );
        },
      },
    });

  const onSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setParams({
      keyword,
      isBelongingEmployee,
      sortType: snakeKeyValue,
      hasBookmark,
      hasReply,
      startDate: formattedStartDate ?? undefined,
      endDate: formattedEndDate ?? undefined,
      employeeIds: [
        ...selectedEmployees.map((ele) => ele.id),
        ...selectedArchivedEmployees.map((ele) => ele.id),
      ].map((ele) => ele),
      businessDivisionIds: selectedDivisions?.map((division) => division.id),
      businessSectionIds: optionSelectedSections?.map((section) => section.id),
      employeeConditionSurveyIds: selectedEmployeeConditionSurveys.map(
        (survey) => survey.value,
      ),
    });
    setFalseRightSidebarMain();
  };

  const onConditionReset = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setKeyword("");
    setParams({
      sortType: snakeKeyValue,
      hasBookmark: undefined,
      hasReply: undefined,
      startDate: undefined,
      endDate: undefined,
      isBelongingEmployee: false,
      employeeIds: [],
      businessDivisionIds: [],
      businessSectionIds: [],
    });
    setBelongingEmployeeFalse();
    setFalseRightSidebarMain();
  };

  useEffect(() => {
    setParams((prevParams) => ({
      ...prevParams,
      sortType: snakeKeyValue,
    }));
  }, [snakeKeyValue]);

  return {
    sort,
    setSort,
    keyword,
    isFetching,
    fetchNextPage,
    hasNextPage,
    startDate,
    endDate,
    employeeConditionSubmissions: items,
    selectableDropdownHasBookmarkIds,
    selectedDropdownHasBookmarkId,
    onChangeDropdownHasBookmarkId,
    selectableDropdownHasReplyIds,
    selectedDropdownHasReplyId,
    onChangeDropdownHasReplyId,
    onChangeKeyword,
    onChangeDateRange,
    onSearchSubmit,
    onConditionReset,
    refetch,
    selectableEmployeeConditionSurveys,
    selectedEmployeeConditionSurveys,
    onChangeEmployeeConditionSurveys,
    onDivisionChange,
    onSectionChange,
    onEmployeesChange,
    onArchivedEmployeesChange,
    selectedDropdownEmployees: selectedEmployees,
    selectableDropdownEmployees: selectableEmployees,
    selectableDropdownDivisions: selectableDivisions,
    selectedDropdownDivisions: selectedDivisions,
    selectableDropdownSections: optionSelectableSections,
    selectedDropdownSections: optionSelectedSections,
    selectableDropdownArchivedEmployees: selectableArchivedEmployees,
    selectedDropdownArchivedEmployees: selectedArchivedEmployees,
    isBelongingEmployee,
    onChangeIsBelongingEmployee,
  };
};
